import React, {MouseEventHandler, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {DesignerSettings, AssetService} from '@taopix/taopix-core/dist/types/types';
import {TextInput, Theme, ThemeName} from '@taopix/taopix-design-system';
import APIServer from '@taopix/taopix-core/dist/types/Service/APIServer';
import classNames from 'classnames';
import {Common} from '@taopix/taopix-core';
import Generic from '../Auth/Buttons/Generic';

export type AuthenticationViewProps = {
  isOpen: boolean;
  closeEvent: MouseEventHandler;
  assetService: AssetService;
  onAuthenticate: Function;
  designerSettings: DesignerSettings;
  server: APIServer;
};

export type AuthResponse = {
  data: {
    url: string;
  };
  error: AuthError;
};

export type TokenResponse = {
  token: string;
  error: AuthError;
};

export type AuthError = {
  code: number;
  fullMessage: string;
  phrase: string;
};

const Authentication = ({
  isOpen,
  closeEvent,
  assetService,
  onAuthenticate,
  designerSettings,
  server,
  ...props
}: AuthenticationViewProps) => {
  const {t} = useTranslation(['assetservices']);

  const [errorData, setErrorData] = useState<AuthError>({} as AuthError);
  const [pending, setPending] = useState<boolean>(false);
  const [authparam1Value, setAuthparam1Value] = useState<string>('');
  const [authparam2Value, setAuthparam2Value] = useState<string>('');
  const authparam1Ref = useRef<HTMLInputElement>(null);
  const authparam2Ref = useRef<HTMLInputElement>(null);

  //TODO
  const onAuthError = (response: any) => {
    setPending(false);
    setErrorData(response.error);
  };

  const onAuth = () => {
    if (pending) {
      return;
    }
    setPending(true);

    let authparam1 = '';
    let authparam2 = '';

    if (authparam1Ref.current) {
      authparam1 = authparam1Ref.current.value;
      setAuthparam1Value(authparam1);
    }

    if (authparam2Ref.current) {
      authparam2 = authparam2Ref.current.value;
      setAuthparam2Value(authparam2);
    }

    let url = `/api/assetservice/${assetService.code}/callback`;
    url += `?ref=${designerSettings.tokens.session}&authparam1=${authparam1}&authparam2=${authparam2}`;

    server
      .get<TokenResponse>(url, {token: 'token'})
      .then(response => {
        if (response.data.token !== '') {
          onAuthenticate(true, response.data.token);
          setPending(false);
        } else {
          setErrorData(response.data.error);
          setPending(false);
        }
      })
      .catch(error => {
        onAuthError(error);
        setPending(false);
      });
  };

  const BannerPicture = () => {
    return assetService.bannerpicturedata !== '' ? (
      <img
        src={
          window.config.baseURL +
          designerSettings.urls.thumbnailPath +
          '/groups/' +
          designerSettings.keySettings.groupCode +
          '/' +
          assetService.bannerpicturedata
        }
      />
    ) : null;
  };

  const BannerDescription = () => {
    return assetService.bannerdescription ? (
      <p>{Common.getCurrentLocaleString(assetService.bannerdescription, true)}</p>
    ) : null;
  };

  const auth = useMemo(() => {
    if (assetService.authenticationmode === 1) {
      onAuth();
    }
    let authparam1label = '';
    let authparam2label = '';

    switch (assetService.authenticationmode) {
      case 2:
        authparam1label = t('str_LabelLogin');
        authparam2label = t('str_LabelPassword');
        break;
      case 3:
        authparam1label = t('str_LabelLogin');
        break;
      case 4:
        authparam1label = t('str_LabelUserName');
        break;
      case 5:
        authparam1label = t('str_LabelPassword');
        break;
      case 6:
        authparam1label = t('str_LabelAuthenticationKey');
        break;
      case 7:
        authparam1label = t('str_LabelCode');
        break;

      default:
        break;
    }

    return (
      <>
        {
          //if its not automatic auth
          assetService.authenticationmode !== 1 ? (
            <TextInput
              label={authparam1label}
              type={'text'}
              name={'authparam1'}
              id={'authparam1'}
              required={true}
              defaultValue={authparam1Value}
              autoFocus
              disabled={pending}
              className={'w-full'}
            />
          ) : (
            <></>
          )
        }

        {assetService.authenticationmode === 2 ? (
          <TextInput
            label={authparam2label}
            type={'password'}
            name={'authparam2'}
            id={'authparam2'}
            required={true}
            defaultValue={authparam2Value}
            disabled={pending}
            className={'w-full'}
          />
        ) : (
          <></>
        )}
      </>
    );
  }, [assetService.authenticationmode]);

  const Error = ({error}: {error: AuthError}) => {
    return (
      <Theme name={ThemeName.Warning} className="px-sm py-xs mt-sm">
        {t('str_ErrorAssetServiceAPI').replace(
          '^0',
          Common.getCurrentLocaleString(assetService.name, true)
        )}
      </Theme>
    );
  };

  return (
    <div className={classNames(['flex', 'flex-col', 'space-y-md', 'm-md', 'items-center'])}>
      {<BannerPicture />}
      {<BannerDescription />}
      {errorData?.code && <Error error={errorData} />}
      {auth}
      <Generic
        onClick={onAuth}
        pending={pending}
        privacyPolicyUrl={Common.getCurrentLocaleString(assetService.privacypolicyurl, true)}
      />
    </div>
  );
};

export default Authentication;
